import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { DogIcon as Dog, UsersFourIcon as UsersFour, PawPrintIcon as PawPrint, ChatCircleIcon as ChatCircle, CalendarIcon as Calendar, UserIcon as User, MagnifyingGlassIcon as MagnifyingGlass, ArrowRightIcon as ArrowRight, CompassIcon as Compass, ReceiptIcon as Receipt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { thumbUrl, formatDate } from "@/lib/utils-app";
import { useAuth } from "@/contexts/AuthContext";

const DEBOUNCE_MS = 220;

/**
 * Curated set of pages to surface in the command palette.
 *
 * Admins see ALL admin sub-pages plus a few high-traffic user surfaces.
 * Non-admins only see user-facing surfaces. The keywords array drives
 * fuzzy text matching when the user types — the page label itself is
 * always included too.
 *
 * Order matters — frequently-visited pages first.
 */
const PAGES_ADMIN = [
  { label: "Admin operations (daily)", href: "/admin", keywords: ["operations", "ops", "dashboard", "daily", "health", "home", "summary", "check"] },
  { label: "Admin console (full tools)", href: "/admin/console", keywords: ["console", "tools", "all admin"] },
  { label: "Operator Binder", href: "/admin/operator-binder", keywords: ["insurance", "broker", "bank", "vendor", "investor", "legal", "documents", "binder", "underwriter", "checklist", "submission"] },
  { label: "Business & insurance classification", href: "/admin/business-classification", keywords: ["insurance", "naics", "broker", "underwriter", "classification", "answer key"] },
  { label: "Launch playbook & IP", href: "/admin/launch-playbook", keywords: ["launch", "playbook", "trademark", "patent", "copyright", "ip"] },
  { label: "Launch pack", href: "/admin/launch-pack", keywords: ["business plan", "insurance", "dossier", "seo", "sample tour", "pdf"] },
  { label: "Roadmap & ideas", href: "/admin/roadmap", keywords: ["backlog", "future", "ideas"] },
  { label: "Press kit", href: "/admin/press-kit", keywords: ["press", "marketing", "blast", "social"] },
  { label: "Launch readiness", href: "/admin/launch-readiness", keywords: ["readiness", "probe", "stripe", "daily.co", "launch"] },
  { label: "Stripe Radar rules", href: "/admin/stripe-radar", keywords: ["stripe", "radar", "fraud"] },
  { label: "Approvals", href: "/admin/approvals", keywords: ["pending", "review"] },
  { label: "Data & users", href: "/admin/data", keywords: ["users", "manage", "moderation"] },
  { label: "Manage events", href: "/admin/events", keywords: ["events"] },
  { label: "Disputes", href: "/admin/disputes", keywords: ["dispute", "refund", "escrow"] },
  { label: "Manage breeds", href: "/admin/breeds", keywords: ["breeds", "catalog"] },
  { label: "Feature matrix (everything the app does)", href: "/admin/feature-matrix", keywords: ["feature", "features", "matrix", "capabilities", "persona", "demo", "list", "what it does", "marketing", "one pager"] },
  { label: "Product analytics", href: "/admin/analytics", keywords: ["analytics", "posthog", "funnel", "revenue", "retention", "metrics", "dashboard"] },
];
const PAGES_COMMON = [
  { label: "Dashboard", href: "/dashboard", keywords: ["home"] },
  { label: "Marketplace", href: "/marketplace", keywords: ["browse", "buy", "sale"] },
  { label: "My dogs", href: "/dogs", keywords: ["registered", "kennel"] },
  { label: "Litters", href: "/litters", keywords: ["puppies", "whelp"] },
  { label: "The Dog's Voice feed", href: "/feed", keywords: ["social", "post", "pawprints", "dog's voice"] },
  { label: "Inbox", href: "/inbox", keywords: ["messages", "chat"] },
  { label: "My video tours", href: "/tours", keywords: ["video", "tour", "daily"] },
  { label: "Billing & plans", href: "/billing", keywords: ["subscription", "pricing", "plan", "pay", "account"] },
  { label: "Watchlist", href: "/watchlist", keywords: ["saved", "watch"] },
  { label: "Settings", href: "/settings", keywords: ["account", "preferences", "2fa"] },
];

/**
 * Unified cross-collection search modal + admin command palette.
 *
 * Hits `GET /api/search?q=…&limit=5` — debounced — and renders results
 * grouped by category. Supports keyboard navigation (Up/Down/Enter,
 * Escape to close). Admins see a Users section and ALL admin pages
 * automatically.
 *
 * Open from anywhere via the global event:
 *   window.dispatchEvent(new CustomEvent("open-search"))
 * which the AppShell binds to its search input + the Cmd+K / "/" shortcut.
 */
export default function SearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const allPages = (user?.role === "admin" ? PAGES_ADMIN : []).concat(PAGES_COMMON);
  const matchedPages = React.useMemo(() => {
    if (q.trim().length === 0) return allPages.slice(0, 8); // suggestions on empty query
    const needle = q.trim().toLowerCase();
    const matched = allPages.filter((p) => {
      const hay = (p.label + " " + (p.keywords || []).join(" ") + " " + p.href).toLowerCase();
      return hay.includes(needle);
    }).slice(0, 8);
    // If the user's query yields no page matches, show the top 4 default
    // pages anyway so the palette never feels "lost" — they can still
    // bounce back to a common destination.
    return matched.length > 0 ? matched : allPages.slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, user?.role]);

  // Reset on open/close so a fresh dialog always starts blank.
  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setActive(0);
      // Defer focus so the dialog has fully mounted.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounced query.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim(), limit: 5 } });
        // For admins, also fetch payments (separate endpoint so non-admins
        // never see Stripe ids). Errors are swallowed so failures here
        // never break the main palette.
        let payments = [];
        if (user?.role === "admin") {
          try {
            const { data: pay } = await api.get("/admin/ops/payments-search", {
              params: { q: q.trim(), limit: 5 },
            });
            payments = pay.payments || [];
          } catch {
            payments = [];
          }
        }
        setResults({ ...data, payments });
        setActive(0);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [q, user?.role]);

  // Flat list of all hits (in render order) for keyboard nav.
  // Pages always come first, then dynamic search results.
  const flatHits = useFlatHits(results, matchedPages);

  const close = () => onOpenChange(false);

  const go = (path) => {
    close();
    navigate(path);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(0, flatHits.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = flatHits[active];
      if (hit) go(hit.href);
    }
  };

  const empty =
    results &&
    !loading &&
    flatHits.length === 0 &&
    q.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden rounded-2xl"
        onKeyDown={onKeyDown}
        data-testid="search-dialog"
      >
        {/* Hidden title for screen readers (Radix a11y requirement). */}
        <DialogTitle className="sr-only">Search and command palette</DialogTitle>
        <div className="flex items-center gap-3 border-b border-[#E5E5E5] px-4 py-3">
          <MagnifyingGlass size={18} weight="bold" className="text-[#6B6B6B] flex-shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Jump to a page or search dogs, breeders, litters, posts…"
            className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-[#6B6B6B] text-ink"
            data-testid="search-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-[#E5E5E5] bg-[#FAFAFA] px-1.5 py-0.5 text-[10px] font-mono text-[#6B6B6B]">
            ESC
          </kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Pages section — always rendered if there are matched pages.
              Doubles as default suggestions when the input is empty. */}
          {matchedPages.length > 0 && (
            <div className="py-2" data-testid="search-pages-section">
              <div className="px-4 py-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                <Compass size={11} weight="bold" />
                {q.trim() ? "Pages" : "Jump to"}
              </div>
              {matchedPages.map((p, i) => (
                <Row
                  key={p.href}
                  active={flatHits[active]?.kind === "page" && flatHits[active]?.id === p.href}
                  onSelect={() => go(p.href)}
                  testid={`search-page-${p.href.replace(/\//g, "-").replace(/^-/, "")}`}
                  iconBg="#FAFAFA"
                  icon={<Compass size={16} weight="duotone" className="text-[#3F4A8A]" />}
                  title={p.label}
                  subtitle={p.href}
                />
              ))}
            </div>
          )}
          {q.trim().length === 0 && matchedPages.length === 0 && (
            <div className="p-8 text-center text-sm text-[#6B6B6B]">
              Type to search dogs, breeders, litters, posts, events…
            </div>
          )}
          {q.trim().length >= 1 && q.trim().length < 2 && (
            <div className="p-4 text-center text-xs text-[#6B6B6B]">
              Type one more character to search dogs &amp; breeders.
            </div>
          )}
          {loading && (
            <div className="p-6 text-center text-sm text-[#6B6B6B]">Searching…</div>
          )}
          {empty && (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-ink">No matches for "{q}"</p>
              <p className="text-xs text-[#6B6B6B] mt-1">
                Try a name, owner, kennel, breed, registration #, or event title.
              </p>
            </div>
          )}
          {results && !loading && flatHits.length > 0 && (
            <div className="py-2">
              {results.dogs.length > 0 && (
                <Section title="Dogs" icon={Dog}>
                  {results.dogs.map((d) => (
                    <DogRow
                      key={d.id}
                      dog={d}
                      active={flatHits[active]?.id === d.id && flatHits[active]?.kind === "dog"}
                      onSelect={() => go(`/dogs/${d.id}`)}
                    />
                  ))}
                </Section>
              )}
              {results.breeders.length > 0 && (
                <Section title="Breeders" icon={UsersFour}>
                  {results.breeders.map((b) => (
                    <Row
                      key={b.id}
                      active={flatHits[active]?.id === b.id && flatHits[active]?.kind === "breeder"}
                      onSelect={() => go(`/breeders/${b.id}`)}
                      testid={`search-breeder-${b.id}`}
                      iconBg={b.elite_verified ? "#FFF0F0" : "#FBF7EC"}
                      icon={<UsersFour size={16} weight="duotone" className={b.elite_verified ? "text-crimson" : "text-gold"} />}
                      title={b.kennel_name || b.name}
                      subtitle={[b.kennel_name && b.name !== b.kennel_name ? b.name : null, b.location].filter(Boolean).join(" · ")}
                      badge={b.elite_verified ? (
                        <span
                          className="inline-flex items-center gap-1 text-[8.5px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#7A1A1A] via-[#C1272D] to-[#7A1A1A] text-white rounded-full px-1.5 py-0.5"
                          data-testid={`search-breeder-elite-${b.id}`}
                          title="Elite Verified Breeder"
                        >
                          Elite
                        </span>
                      ) : null}
                    />
                  ))}
                </Section>
              )}
              {results.litters.length > 0 && (
                <Section title="Litters" icon={PawPrint}>
                  {results.litters.map((l) => (
                    <Row
                      key={l.id}
                      active={flatHits[active]?.id === l.id && flatHits[active]?.kind === "litter"}
                      onSelect={() => go(`/litters/${l.id}`)}
                      testid={`search-litter-${l.id}`}
                      iconBg="#FAFAFA"
                      icon={<PawPrint size={16} weight="duotone" className="text-[#6B6B6B]" />}
                      title={l.name || `${l.sire_name || "?"} × ${l.dam_name || "?"}`}
                      subtitle={l.whelp_date ? `Whelped ${formatDate(l.whelp_date)}` : ""}
                    />
                  ))}
                </Section>
              )}
              {results.posts.length > 0 && (
                <Section title="The Dog's Voice" icon={ChatCircle}>
                  {results.posts.map((p) => (
                    <Row
                      key={p.id}
                      active={flatHits[active]?.id === p.id && flatHits[active]?.kind === "post"}
                      onSelect={() => go(`/feed`)}
                      testid={`search-post-${p.id}`}
                      iconBg="#FBF7EC"
                      icon={
                        p.dog_photo ? (
                          <img src={thumbUrl(p.dog_photo)} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <PawPrint size={16} weight="duotone" className="text-[#6B6B6B]" />
                        )
                      }
                      title={p.dog_name || "A dog"}
                      subtitle={p.text}
                    />
                  ))}
                </Section>
              )}
              {results.events.length > 0 && (
                <Section title="Events" icon={Calendar}>
                  {results.events.map((e) => (
                    <Row
                      key={e.id}
                      active={flatHits[active]?.id === e.id && flatHits[active]?.kind === "event"}
                      onSelect={() => go(`/events/${e.id}`)}
                      testid={`search-event-${e.id}`}
                      iconBg="#FBF7EC"
                      icon={<Calendar size={16} weight="duotone" className="text-gold" />}
                      title={e.title}
                      subtitle={[e.location, e.start_date && formatDate(e.start_date)].filter(Boolean).join(" · ")}
                    />
                  ))}
                </Section>
              )}
              {results.users.length > 0 && (
                <Section title="Users (admin)" icon={User}>
                  {results.users.map((u) => (
                    <Row
                      key={u.id}
                      active={flatHits[active]?.id === u.id && flatHits[active]?.kind === "user"}
                      onSelect={() => go(`/admin/data?tab=users&q=${encodeURIComponent(u.email)}`)}
                      testid={`search-user-${u.id}`}
                      iconBg="#FAFAFA"
                      icon={<User size={16} weight="duotone" className="text-[#6B6B6B]" />}
                      title={u.name || u.email}
                      subtitle={`${u.email}${u.role === "admin" ? " · admin" : ""}${u.suspended ? " · suspended" : ""}`}
                    />
                  ))}
                </Section>
              )}
              {(results.payments || []).length > 0 && (
                <Section title="Payments (admin)" icon={Receipt}>
                  {results.payments.map((p) => (
                    <Row
                      key={p.id || p.session_id}
                      active={flatHits[active]?.id === (p.id || p.session_id) && flatHits[active]?.kind === "payment"}
                      onSelect={() => go("/admin/payments")}
                      testid={`search-payment-${p.id || p.session_id}`}
                      iconBg="#FAFAFA"
                      icon={<Receipt size={16} weight="duotone" className={p.payment_status === "paid" ? "text-emerald-700" : "text-[#C1272D]"} />}
                      title={`$${(p.amount_usd || 0).toFixed(2)} · ${(p.package_id || p.kind || "").replace(/_/g, " ")}`}
                      subtitle={`${p.payment_status || "unknown"} · ${(p.session_id || "").slice(0, 24)}…`}
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function useFlatHits(results, matchedPages) {
  return React.useMemo(() => {
    const out = [];
    for (const p of (matchedPages || [])) {
      out.push({ kind: "page", id: p.href, href: p.href });
    }
    if (!results) return out;
    for (const d of results.dogs) out.push({ kind: "dog", id: d.id, href: `/dogs/${d.id}` });
    for (const b of results.breeders) out.push({ kind: "breeder", id: b.id, href: `/breeders/${b.id}` });
    for (const l of results.litters) out.push({ kind: "litter", id: l.id, href: `/litters/${l.id}` });
    for (const p of results.posts) out.push({ kind: "post", id: p.id, href: `/feed` });
    for (const e of results.events) out.push({ kind: "event", id: e.id, href: `/events/${e.id}` });
    for (const u of results.users) out.push({ kind: "user", id: u.id, href: `/admin/data?tab=users&q=${encodeURIComponent(u.email)}` });
    for (const p of (results.payments || [])) out.push({ kind: "payment", id: p.id || p.session_id, href: "/admin/payments" });
    return out;
  }, [results, matchedPages]);
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="py-1.5">
      <div className="px-4 py-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
        <Icon size={11} weight="bold" />
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ active, onSelect, testid, icon, iconBg, title, subtitle, badge }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        active ? "bg-[#FAFAFA]" : "hover:bg-[#FAFAFA]"
      }`}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{ background: iconBg || "#FAFAFA" }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold text-ink truncate">{title}</p>
          {badge}
        </div>
        {subtitle && <p className="text-xs text-[#6B6B6B] truncate">{subtitle}</p>}
      </div>
      <ArrowRight size={12} weight="bold" className="text-[#C4C4C4] flex-shrink-0" />
    </button>
  );
}

function DogRow({ dog, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`search-dog-${dog.id}`}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        active ? "bg-[#FAFAFA]" : "hover:bg-[#FAFAFA]"
      }`}
    >
      <div className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden bg-[#FAFAFA]">
        {dog.photo ? (
          <img src={thumbUrl(dog.photo)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog size={14} weight="duotone" className="text-[#6B6B6B]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink truncate">
          {dog.name}
          {dog.registration_number && (
            <span className="ml-2 text-[10px] font-mono font-normal text-[#6B6B6B]">
              {dog.registration_number}
            </span>
          )}
        </p>
        <p className="text-xs text-[#6B6B6B] truncate">{dog.breed}</p>
      </div>
      <ArrowRight size={12} weight="bold" className="text-[#C4C4C4] flex-shrink-0" />
    </button>
  );
}
